import React, { useState, useMemo } from 'react';
import { Search, ChevronLeft, ChevronRight, Smartphone, Check, X, Shield, User } from 'lucide-react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../../components/ui/Table";
import { Badge } from "../../../components/ui/Badge";
import { IconButton } from "../../../components/ui/Button";
import { Select } from "../../../components/ui/Select";

const UserList = ({ users, roles, onUpdateUser, onOpenDevices }) => {
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [updatingId, setUpdatingId] = useState(null); // ID of user currently being updated

    // Filter users based on search
    const filteredUsers = useMemo(() => {
        if (!search.trim()) return users;
        const lowerSearch = search.toLowerCase();
        return users.filter(user =>
            user.username?.toLowerCase().includes(lowerSearch) ||
            user.email?.toLowerCase().includes(lowerSearch) ||
            user.id?.toString().includes(lowerSearch)
        );
    }, [users, search]);

    // Pagination logic
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const currentUsers = filteredUsers.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const getRoleValue = (role) => {
        if (typeof role === 'string') return role;
        return role?.name || role?.role_name || role?.toString() || role;
    };

    const getRoleLabel = (role) => {
        if (typeof role === 'string') return role;
        return role?.name || role?.role_name || role?.toString() || 'Unknown';
    };

    const handleRoleChange = async (userId, newRole) => {
        if (!userId || !newRole) return;
        setUpdatingId(userId);
        await onUpdateUser(userId, { role: newRole });
        setUpdatingId(null);
    };

    const handleStatusChange = async (userId, newStatus) => {
        if (!userId) return;
        setUpdatingId(userId);
        await onUpdateUser(userId, { isActive: newStatus });
        setUpdatingId(null);
    };

    const isUserActive = (user) => {
        // Handle various truthy values from backend (1, '1', true, "true")
        const val = user.isActive ?? user.is_active;
        if (val === true || val === 'true' || val === 1 || val === '1') return true;
        return false;
    };

    return (
        <div className="space-y-4">
            {/* Toolbar: Search */}
            <div className="flex items-center justify-between gap-4 bg-[#1a1a1a] p-4 rounded-xl border border-white/10">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                        type="text"
                        placeholder="Kullanıcı adı, e-posta veya ID ile ara..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setCurrentPage(1); // Reset to first page on search
                        }}
                        className="w-full bg-[#0a0e13] border border-white/10 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:border-orange-500 transition-colors text-sm"
                    />
                </div>
                <div className="text-sm text-gray-400">
                    Toplam <strong>{filteredUsers.length}</strong> kullanıcı
                </div>
            </div>

            {/* Table */}
            <div className="bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden shadow-lg">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-20">ID</TableHead>
                            <TableHead>Kullanıcı</TableHead>
                            <TableHead>E-posta</TableHead>
                            <TableHead className="w-48">Rol</TableHead>
                            <TableHead className="w-32">Durum</TableHead>
                            <TableHead className="w-32 text-right">İşlemler</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {currentUsers.length > 0 ? (
                            currentUsers.map((user) => (
                                <TableRow key={user.id} className="group">
                                    <TableCell className="text-gray-400 font-mono text-xs">{user.id}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-orange-500/10 flex items-center justify-center text-orange-500 font-bold text-xs uppercase">
                                                {user.username?.substring(0, 2) || '??'}
                                            </div>
                                            <span className="font-medium text-white">{user.username}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-gray-300 text-sm">
                                        {user.email || <span className="text-gray-600 italic">E-posta yok</span>}
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            icon={<Shield className="w-3.5 h-3.5" />}
                                            value={getRoleValue(user.role || user.roles?.[0])}
                                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                            disabled={updatingId === user.id}
                                            className={updatingId === user.id ? 'opacity-50' : ''}
                                        >
                                            {roles.map((role, idx) => (
                                                <option key={idx} value={getRoleValue(role)}>
                                                    {getRoleLabel(role)}
                                                </option>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={isUserActive(user)}
                                                onChange={(e) => handleStatusChange(user.id, e.target.checked)}
                                                disabled={updatingId === user.id}
                                            />
                                            <div className="w-9 h-5 bg-gray-700 peer-focus:outline-none ring-0 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                                            <span className={`ml-2 text-xs font-medium`}>
                                                {isUserActive(user) ? (
                                                    <Badge variant="success" className="bg-transparent pl-0 hover:bg-transparent">Aktif</Badge>
                                                ) : (
                                                    <Badge variant="secondary" className="bg-transparent pl-0 hover:bg-transparent text-gray-500">Pasif</Badge>
                                                )}
                                            </span>
                                        </label>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <IconButton
                                            onClick={() => onOpenDevices(user)}
                                            icon={Smartphone}
                                            title="Cihazları Görüntüle"
                                            variant="ghost"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                                    <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>Kullanıcı bulunamadı.</p>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>

                {/* Pagination Footer */}
                {totalPages > 1 && (
                    <div className="p-4 border-t border-white/10 bg-[#252525] flex items-center justify-between">
                        <div className="text-xs text-gray-400">
                            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} entries
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="p-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                // Simple logic to show pages around current page could be added,
                                // for now just showing first 5 or simpler logic if needed.
                                // Let's implement a simple sliding window or just basic numbers.
                                // Actually, let's keep it simple: Show numbers if total pages < 7, else show current and surroundings.
                                // Implementing full pagination logic in one file without external library is verbose.
                                // Let's just show current page number and arrows for now.
                                return null;
                            })}
                            <span className="text-sm text-gray-300 font-mono px-2">
                                Page {currentPage} of {totalPages}
                            </span>

                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="p-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserList;
